import { useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import {
  faBolt,
  faChartLine,
  faCheck,
  faClock,
  faCreditCard,
  faHeadset,
  faMobileScreenButton,
  faPercent,
  faQrcode,
  faRobot,
  faShieldHalved,
  faStore,
  faTruckFast,
  faUsers,
} from "@fortawesome/free-solid-svg-icons"
import { faGoogle, faInstagram, faMeta, faOpenai, faWhatsapp } from "@fortawesome/free-brands-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Navbar from "../../components/LandPage/Navbar"
import { PATH_MAP } from "../../configuration/PathMap"
import { SEO } from "../../seo/Seo"
import { softwareSchema, useOrganizationSchema } from "../../seo/schema"
import LandingYoutubeFrame from "../../components/LandPage/LandingYoutubeFrame"
import Pricing from "../../components/LandPage/Pricing"
import LeadCaptureForm from "../../components/LandPage/LeadCaptureForm"
import Animate, { AnimationTypes } from "../../components/Animate/Animate"
import { trackMarketingEvent } from "../../analytics/tracking"
import { useGetLandingVideos } from "../../hooks/Landing/useGetLandingVideos"
import { useSignUpContext } from "../../context/useSignUpContext"
import { SignUp } from "../../Objects/SignUp/SignUp"
import { extractYoutubeId } from "../../Objects/Generics/Helps"
import { resolveAudienceImagePath } from "./audienceImages"
import { getLandingAnimationDelay } from "./landingPageUtils"
import {
  PublicAnimatedBackground,
  publicAnimatedBackgroundShellClassName,
} from "../../components/PublicAnimatedBackground/PublicAnimatedBackground"

type CardContent = { title: string; description: string }
type AudienceImageContent = { slug: string; badge: string; heroImagePath: string }

const featureIcons = [faQrcode, faTruckFast, faCreditCard, faRobot, faChartLine, faHeadset]

const valueIcons = [faStore, faClock, faUsers, faPercent, faShieldHalved, faMobileScreenButton]

const essentialIcons = [faQrcode, faWhatsapp, faMeta, faCreditCard, faTruckFast, faOpenai, faGoogle, faInstagram]

const mercadoPagoIconUrl = "https://img.icons8.com/color/96/mercado-pago.png"

const landingAnimationDuration = "1.25s"

const landingHeroAnimationDuration = "2s"

const solutionCarouselIntervalMs = 3000

export default function DigitalMenuLandingPage() {
  const organizationSchema = useOrganizationSchema()

  const { t } = useTranslation("seoLanding")

  const navigate = useNavigate()

  const { updateSignUp } = useSignUpContext()

  const digitalMenuKeywords = t("digital_menu.keywords", { returnObjects: true }) as string[]

  const pains = t("digital_menu.pains", { returnObjects: true }) as CardContent[]

  const solutionSteps = t("digital_menu.solution_steps", { returnObjects: true }) as CardContent[]

  const conversionFeatures = t("digital_menu.conversion_features", { returnObjects: true }) as CardContent[]

  const implementationOptions = t("digital_menu.implementation_options", { returnObjects: true }) as CardContent[]

  const valueStack = t("digital_menu.value_stack", { returnObjects: true }) as CardContent[]

  const objections = t("digital_menu.objections", { returnObjects: true }) as CardContent[]

  const beforeAfter = t("digital_menu.before_after", { returnObjects: true }) as CardContent[]

  const essentialItems = t("digital_menu.essential_items", { returnObjects: true }) as CardContent[]

  const volumeProofs = t("digital_menu.volume_proofs", { returnObjects: true }) as CardContent[]

  const audienceItems = t("audiences.items", { returnObjects: true }) as AudienceImageContent[]

  const { handleGetLandingVideos } = useGetLandingVideos()

  const [publicMenuYoutubeUrl, setPublicMenuYoutubeUrl] = useState<string | null>(null)

  const [solutionImageIndex, setSolutionImageIndex] = useState(0)

  const solutionImages = useMemo(
    () =>
      audienceItems
        .map((item) => ({
          alt: item.badge,
          src: resolveAudienceImagePath(item.heroImagePath),
        }))
        .filter((item) => item.src),
    [audienceItems],
  )

  const currentSolutionImage = solutionImages[solutionImageIndex % Math.max(solutionImages.length, 1)]

  const publicMenuVideoId = extractYoutubeId(publicMenuYoutubeUrl ?? "")

  useEffect(() => {
    handleGetLandingVideos()
      .then((value) => setPublicMenuYoutubeUrl(value.publicMenuYoutubeUrl))
      .catch(() => setPublicMenuYoutubeUrl(null))
  }, [handleGetLandingVideos])

  useEffect(() => {
    if (solutionImages.length <= 1) {
      return
    }

    const intervalId = window.setInterval(() => {
      setSolutionImageIndex((current) => (current + 1) % solutionImages.length)
    }, solutionCarouselIntervalMs)

    return () => window.clearInterval(intervalId)
  }, [solutionImages.length])

  useEffect(() => {
    trackMarketingEvent("landing_view_content", {
      page: "digital_menu",
      offer: "done_for_you_menu_setup",
    })
  }, [])

  const handleLeadFocus = (target: string) => {
    trackMarketingEvent("landing_cta_click", { page: "digital_menu", target })
    document.getElementById("lead-capture")?.scrollIntoView({ behavior: "smooth", block: "start" })
  }

  const handleCreateAccount = (target: string) => {
    trackMarketingEvent("landing_cta_click", { page: "digital_menu", target })
    updateSignUp(new SignUp())
    navigate(PATH_MAP.ROOT_SIGN_UP)
  }

  const handleViewExamples = (target: string) => {
    trackMarketingEvent("landing_cta_click", { page: "digital_menu", target })
    navigate(PATH_MAP.RESTAURANT_SEARCH_PAGE)
  }

  return (
    <div
      className={`${publicAnimatedBackgroundShellClassName} h-dvh bg-white bg-cover bg-center relative z-0 overflow-x-hidden overflow-y-scroll scrollbar-none`}
    >
      <PublicAnimatedBackground />

      <SEO
        title={t("digital_menu.seo.title")}
        description={t("digital_menu.seo.description")}
        path={PATH_MAP.DIGITAL_MENU_LANDING_PAGE}
        keywords={digitalMenuKeywords}
        structuredData={[organizationSchema, softwareSchema]}
      />

      <Animate type={AnimationTypes.slideDownIn} duration="1.2s">
        <Navbar showFunctions={false} />
      </Animate>

      <main className="pb-20">
        <section className="relative overflow-hidden bg-white pt-28">
          <div className="mx-auto grid max-w-[1280px] gap-8 px-6 pb-12 text-center lg:grid-cols-[1.02fr_0.98fr] lg:items-center lg:pb-16 lg:text-left">
            <Animate type={AnimationTypes.slideRightIn} duration={landingHeroAnimationDuration}>
              <div className="flex flex-col items-center">
                <span className="inline-flex mb-4 rounded-full bg-orange-50 px-4 py-1 text-xs font-black uppercase tracking-wide text-orange-700">
                  {t("digital_menu.badge")}
                </span>

                <h1 className="max-w-3xl text-center text-4xl font-black leading-tight text-slate-950 lg:text-6xl">
                  {t("digital_menu.hero_title")}
                </h1>

                <p className="mt-4 max-w-2xl text-justify text-base leading-relaxed text-slate-600 lg:text-lg">
                  {t("digital_menu.hero_description")}
                </p>

                <div className="mt-7 grid w-full max-w-2xl gap-3 sm:grid-cols-2">
                  {essentialItems.map((item, index) => (
                    <Animate
                      key={item.title}
                      type={AnimationTypes.fadeInTraslate}
                      duration="1.15s"
                      delay={getLandingAnimationDelay(index)}
                    >
                      <article className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-left">
                        <div className="mb-2 flex items-center gap-2">
                          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-white text-orange-600 shadow-sm">
                            {index === 3 ? (
                              <img src={mercadoPagoIconUrl} alt={item.title} className="h-5 w-5 object-contain" />
                            ) : (
                              <FontAwesomeIcon icon={essentialIcons[index] ?? faCheck} />
                            )}
                          </span>
                          <h2 className="text-sm font-black text-slate-900">{item.title}</h2>
                        </div>
                        <p className="text-justify text-xs leading-relaxed text-slate-600">{item.description}</p>
                      </article>
                    </Animate>
                  ))}
                </div>

                <div className="mt-4 grid w-full max-w-2xl gap-3 sm:grid-cols-2">
                  {volumeProofs.map((item, index) => (
                    <Animate
                      key={item.title}
                      type={AnimationTypes.zoomIn}
                      duration="1.2s"
                      delay={getLandingAnimationDelay(index)}
                    >
                      <article className="rounded-xl border border-orange-200 bg-orange-50 px-4 py-3">
                        <h2 className="text-center text-lg font-black text-orange-800">{item.title}</h2>
                        <p className="mt-1 text-justify text-xs leading-relaxed text-orange-900">{item.description}</p>
                      </article>
                    </Animate>
                  ))}
                </div>

                <Animate type={AnimationTypes.slideUpIn} duration="1.3s" delay="0.25s">
                  <div className="mt-7 flex flex-col gap-3 sm:flex-row">
                    <button
                      onClick={() => handleLeadFocus("done_for_you_setup")}
                      className="inline-flex cursor-pointer items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-red-600 to-orange-500 px-6 py-4 text-base font-black text-white shadow-lg shadow-red-500/30 transition hover:scale-[1.01] active:scale-95"
                      data-interaction-description-key="interactionDescriptions:pages.landPage.digitalMenuLandingPage.button.handleLeadFocusDoneForYouSetup"
                    >
                      <FontAwesomeIcon icon={faBolt} />
                      {t("digital_menu.cta_done_for_you")}
                    </button>

                    <button
                      onClick={() => handleCreateAccount("create_account")}
                      className="inline-flex cursor-pointer items-center justify-center rounded-xl bg-gradient-to-r from-sky-600 to-indigo-600 px-6 py-4 text-base font-black text-white shadow-lg shadow-sky-500/25 transition hover:from-sky-700 hover:to-indigo-700 active:scale-95"
                      data-interaction-description-key="interactionDescriptions:pages.landPage.digitalMenuLandingPage.button.handleCreateAccountCreateAccount"
                    >
                      {t("digital_menu.cta_create")}
                    </button>
                  </div>
                </Animate>
              </div>
            </Animate>

            <Animate type={AnimationTypes.slideLeftIn} duration={landingHeroAnimationDuration} delay="0.12s">
              <div className="grid gap-5">
                <LeadCaptureForm
                  i18nPrefix="digital_menu_lead_form"
                  requireEmailField={false}
                  showBusinessNameField={false}
                  showEmailField={false}
                  showTrafficSourceField={false}
                  source="digital_menu_done_for_you"
                  trafficSourceFallback="Instagram Ads - cardapio digital"
                  variant="compact"
                />

                {currentSolutionImage && (
                  <Animate type={AnimationTypes.zoomIn} duration="1.35s" delay="0.2s">
                    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 shadow-sm">
                      <div className="relative aspect-[4/3] overflow-hidden bg-slate-100 sm:aspect-[16/10]">
                        <Animate key={currentSolutionImage.src} type={AnimationTypes.slideRightIn} duration="1s">
                          <div className="absolute inset-0">
                            <img
                              src={currentSolutionImage.src}
                              alt={currentSolutionImage.alt}
                              className="h-full w-full object-fit"
                              loading="eager"
                            />

                            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-slate-950/80 to-transparent p-4">
                              <p className="text-center text-sm font-black text-white">{currentSolutionImage.alt}</p>
                            </div>
                          </div>
                        </Animate>

                        <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-white/10 via-transparent to-black/10" />

                        <div className="absolute inset-x-4 bottom-2 flex gap-1.5">
                          {solutionImages.slice(0, 8).map((item) => (
                            <span
                              key={item.src}
                              className={`h-1 flex-1 rounded-full transition-colors duration-500 ${
                                item.src === currentSolutionImage.src ? "bg-white" : "bg-white/35"
                              }`}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  </Animate>
                )}
              </div>
            </Animate>
          </div>
        </section>

        <section className="bg-slate-50 py-14">
          <div className="mx-auto max-w-[1280px] px-6">
            <Animate type={AnimationTypes.fadeInTraslate} duration={landingAnimationDuration}>
              <div className="mx-auto max-w-3xl text-center">
                <span className="text-xs font-black uppercase tracking-wide text-red-600">
                  {t("digital_menu.pains_badge")}
                </span>
                <h2 className="mt-2 text-3xl font-black text-slate-950">{t("digital_menu.pains_title")}</h2>
                <p className="mt-4 text-justify text-base leading-relaxed text-slate-600">
                  {t("digital_menu.pains_description")}
                </p>
              </div>
            </Animate>

            <div className="mt-7 grid gap-4 md:grid-cols-3">
              {pains.map((pain, index) => (
                <Animate
                  key={pain.title}
                  type={AnimationTypes.slideUpIn}
                  duration="1.2s"
                  delay={getLandingAnimationDelay(index)}
                >
                  <article className="rounded-xl border border-red-100 bg-white p-5 shadow-sm">
                    <div className="mx-auto mb-4 flex h-9 w-9 items-center justify-center rounded-full bg-red-50 text-sm font-black text-red-600">
                      {index + 1}
                    </div>
                    <h3 className="text-center text-lg font-black text-slate-900">{pain.title}</h3>
                    <p className="mt-3 text-justify text-sm leading-relaxed text-slate-600">{pain.description}</p>
                  </article>
                </Animate>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14">
          <div className="mx-auto grid max-w-[1280px] gap-8 px-6 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
            <Animate type={AnimationTypes.slideRightIn} duration={landingAnimationDuration}>
              <div className="flex flex-col items-center">
                <span className="text-xs font-black uppercase tracking-wide text-emerald-700">
                  {t("digital_menu.solution_badge")}
                </span>
                <h2 className="mt-2 text-center text-3xl font-black text-slate-950">
                  {t("digital_menu.solution_title")}
                </h2>
                <p className="mt-4 text-justify text-base leading-relaxed text-slate-600">
                  {t("digital_menu.solution_description")}
                </p>

                <Animate type={AnimationTypes.zoomIn} duration="1.15s" delay="0.15s">
                  <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                    <button
                      onClick={() => handleLeadFocus("solution_section_setup")}
                      className="inline-flex cursor-pointer items-center justify-center rounded-xl bg-slate-950 px-5 py-3 text-sm font-black text-white transition hover:bg-slate-800"
                      data-interaction-description-key="interactionDescriptions:pages.landPage.digitalMenuLandingPage.button.handleLeadFocusSolutionSectionSetup"
                    >
                      {t("digital_menu.cta_done_for_you_short")}
                    </button>
                  </div>
                </Animate>
              </div>
            </Animate>

            <div className="grid gap-4 md:grid-cols-3">
              {solutionSteps.map((step, index) => (
                <Animate
                  key={step.title}
                  type={AnimationTypes.slideUpIn}
                  duration="1.2s"
                  delay={getLandingAnimationDelay(index)}
                >
                  <article className="rounded-xl border border-emerald-100 bg-emerald-50/40 p-5 shadow-sm">
                    <div className="mx-auto mb-4 flex h-9 w-9 items-center justify-center rounded-full bg-emerald-700 text-sm font-black text-white">
                      {index + 1}
                    </div>
                    <h3 className="text-center font-black text-slate-900">{step.title}</h3>
                    <p className="mt-3 text-justify text-sm leading-relaxed text-slate-600">{step.description}</p>
                  </article>
                </Animate>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-slate-950 py-14 text-white">
          <div className="mx-auto max-w-[1280px] px-6">
            <Animate type={AnimationTypes.fadeInTraslate} duration={landingAnimationDuration}>
              <div className="mx-auto max-w-3xl text-center">
                <span className="text-xs font-black uppercase tracking-wide text-orange-300">
                  {t("digital_menu.features_badge")}
                </span>
                <h2 className="mt-2 text-3xl font-black">{t("digital_menu.features_title")}</h2>
                <p className="mt-4 text-justify text-sm leading-relaxed text-slate-300">
                  {t("digital_menu.features_description")}
                </p>
              </div>
            </Animate>

            <div className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {conversionFeatures.map((feature, index) => (
                <Animate
                  key={feature.title}
                  type={AnimationTypes.zoomIn}
                  duration="1.18s"
                  delay={getLandingAnimationDelay(index)}
                >
                  <article className="rounded-xl border border-white/10 bg-white/5 p-5">
                    <div className="mx-auto mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-white text-orange-600">
                      <FontAwesomeIcon icon={featureIcons[index] ?? faMobileScreenButton} />
                    </div>
                    <h3 className="text-center font-black">{feature.title}</h3>
                    <p className="mt-3 text-justify text-sm leading-relaxed text-slate-300">{feature.description}</p>
                  </article>
                </Animate>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-white py-14">
          <div className="mx-auto max-w-[1280px] px-6">
            <Animate type={AnimationTypes.fadeInTraslate} duration={landingAnimationDuration}>
              <div className="mx-auto max-w-3xl text-center">
                <span className="text-xs font-black uppercase tracking-wide text-red-600">
                  {t("digital_menu.value_badge")}
                </span>
                <h2 className="mt-2 text-3xl font-black text-slate-950">{t("digital_menu.value_title")}</h2>
                <p className="mt-4 text-justify text-sm leading-relaxed text-slate-600">
                  {t("digital_menu.value_description")}
                </p>
              </div>
            </Animate>

            <div className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {valueStack.map((item, index) => (
                <Animate
                  key={item.title}
                  type={AnimationTypes.fadeInTraslate}
                  duration="1.18s"
                  delay={getLandingAnimationDelay(index)}
                >
                  <article className="rounded-xl border border-slate-200 bg-slate-50 p-5">
                    <div className="mx-auto mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-white text-red-600 shadow-sm">
                      <FontAwesomeIcon icon={valueIcons[index] ?? faCheck} />
                    </div>
                    <h3 className="text-center font-black text-slate-900">{item.title}</h3>
                    <p className="mt-3 text-justify text-sm leading-relaxed text-slate-600">{item.description}</p>
                  </article>
                </Animate>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-orange-50 py-14">
          <div className="mx-auto max-w-[1280px] px-6">
            <Animate type={AnimationTypes.fadeInTraslate} duration={landingAnimationDuration}>
              <h2 className="mx-auto max-w-3xl text-center text-3xl font-black text-slate-950">
                {t("digital_menu.before_after_title")}
              </h2>
            </Animate>

            <div className="mt-8 grid gap-6 lg:grid-cols-2">
              {beforeAfter.map((item, index) => (
                <Animate
                  key={item.title}
                  type={index === 0 ? AnimationTypes.slideRightIn : AnimationTypes.slideLeftIn}
                  duration="1.25s"
                  delay={getLandingAnimationDelay(index)}
                >
                  <article
                    className={`rounded-2xl border p-6 shadow-sm ${
                      index === 0 ? "border-red-200 bg-white" : "border-emerald-200 bg-emerald-50"
                    }`}
                  >
                    <span
                      className={`block text-center text-xs font-black uppercase tracking-wide ${
                        index === 0 ? "text-red-600" : "text-emerald-700"
                      }`}
                    >
                      {index === 0 ? t("digital_menu.before_badge") : t("digital_menu.after_badge")}
                    </span>
                    <h3 className="mt-2 text-center text-2xl font-black text-slate-950">{item.title}</h3>
                    <p className="mt-3 text-justify text-sm leading-relaxed text-slate-700">{item.description}</p>
                  </article>
                </Animate>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14">
          <div className="mx-auto max-w-[1280px] px-6">
            <Animate type={AnimationTypes.fadeInTraslate} duration={landingAnimationDuration}>
              <div className="mx-auto max-w-3xl text-center">
                <h2 className="text-3xl font-black text-slate-950">{t("digital_menu.implementation_title")}</h2>
                <p className="mt-4 text-justify text-sm leading-relaxed text-slate-600">
                  {t("digital_menu.implementation_description")}
                </p>
              </div>
            </Animate>

            <div className="mt-8 grid gap-6 lg:grid-cols-2">
              {implementationOptions.map((option, index) => (
                <Animate
                  key={option.title}
                  type={AnimationTypes.zoomIn}
                  duration="1.22s"
                  delay={getLandingAnimationDelay(index)}
                >
                  <article
                    className={`rounded-2xl border p-6 text-center shadow-sm ${
                      index === 0 ? "border-emerald-200 bg-emerald-50" : "border-sky-200 bg-sky-50"
                    }`}
                  >
                    <h3 className="text-2xl font-black text-slate-950">{option.title}</h3>
                    <p className="mt-3 text-justify text-sm leading-relaxed text-slate-700">{option.description}</p>
                    <button
                      onClick={() =>
                        index === 0
                          ? handleLeadFocus("implementation_done_for_you")
                          : handleCreateAccount("implementation_self_service")
                      }
                      className={`mt-5 inline-flex w-full cursor-pointer items-center justify-center rounded-xl px-5 py-3 text-sm font-black transition sm:w-auto ${
                        index === 0
                          ? "bg-emerald-700 text-white hover:bg-emerald-800"
                          : "bg-gradient-to-r from-sky-600 to-indigo-600 text-white shadow-lg shadow-sky-500/20 hover:from-sky-700 hover:to-indigo-700"
                      }`}
                      data-interaction-description-key={
                        index === 0
                          ? "interactionDescriptions:pages.landPage.digitalMenuLandingPage.button.requestConfiguredMenu"
                          : "interactionDescriptions:pages.landPage.digitalMenuLandingPage.button.createSelfServiceAccount"
                      }
                    >
                      {index === 0 ? t("digital_menu.cta_done_for_you_short") : t("digital_menu.cta_create_short")}
                    </button>
                  </article>
                </Animate>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-slate-50 py-14">
          <div className="mx-auto max-w-[1280px] px-6">
            <Animate type={AnimationTypes.fadeInTraslate} duration={landingAnimationDuration}>
              <div className="mx-auto max-w-3xl text-center">
                <span className="text-xs font-black uppercase tracking-wide text-orange-700">
                  {t("digital_menu.objections_badge")}
                </span>
                <h2 className="mt-2 text-3xl font-black text-slate-950">{t("digital_menu.objections_title")}</h2>
                <p className="mt-4 text-justify text-sm leading-relaxed text-slate-600">
                  {t("digital_menu.objections_description")}
                </p>
              </div>
            </Animate>

            <div className="mt-8 grid gap-4 md:grid-cols-3">
              {objections.map((item) => (
                <Animate key={item.title} type={AnimationTypes.slideUpIn} duration="1.18s">
                  <article className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                    <h3 className="text-center font-black text-slate-900">{item.title}</h3>
                    <p className="mt-3 text-justify text-sm leading-relaxed text-slate-600">{item.description}</p>
                  </article>
                </Animate>
              ))}
            </div>

            <Animate type={AnimationTypes.zoomIn} duration="1.25s" delay="0.16s">
              <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
                <button
                  onClick={() => handleLeadFocus("objections_done_for_you")}
                  className="inline-flex cursor-pointer items-center justify-center rounded-xl bg-gradient-to-r from-red-600 to-orange-500 px-5 py-3 text-sm font-black text-white shadow-lg shadow-red-500/20 transition hover:scale-[1.01]"
                  data-interaction-description-key="interactionDescriptions:pages.landPage.digitalMenuLandingPage.button.handleLeadFocusObjectionsDoneForYou"
                >
                  {t("digital_menu.cta_done_for_you_short")}
                </button>

                <button
                  onClick={() => handleCreateAccount("objections_self_service")}
                  className="inline-flex cursor-pointer items-center justify-center rounded-xl bg-gradient-to-r from-sky-600 to-indigo-600 px-5 py-3 text-sm font-black text-white shadow-lg shadow-sky-500/20 transition hover:from-sky-700 hover:to-indigo-700"
                  data-interaction-description-key="interactionDescriptions:pages.landPage.digitalMenuLandingPage.button.handleCreateAccountObjectionsSelfService"
                >
                  {t("digital_menu.cta_create_short")}
                </button>
              </div>
            </Animate>
          </div>
        </section>

        <section className="bg-white py-12">
          <Animate type={AnimationTypes.fadeInTraslate} duration={landingAnimationDuration}>
            <div className="mx-auto max-w-3xl px-6 text-center">
              <span className="text-xs font-black uppercase tracking-wide text-slate-500">
                {t("digital_menu.examples_badge")}
              </span>
              <h2 className="mt-2 text-3xl font-black text-slate-950">{t("digital_menu.examples_title")}</h2>
              <p className="mt-4 text-justify text-sm leading-relaxed text-slate-600">
                {t("digital_menu.examples_description")}
              </p>
              <button
                type="button"
                onClick={() => handleViewExamples("bottom_examples")}
                className="mt-6 inline-flex w-full cursor-pointer items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-5 py-3 text-sm font-black text-slate-800 transition hover:bg-slate-50 sm:w-auto"
                data-interaction-description-key="interactionDescriptions:pages.landPage.digitalMenuLandingPage.button.handleViewExamplesBottomExamples"
              >
                <FontAwesomeIcon icon={faMobileScreenButton} />
                {t("digital_menu.cta_example")}
              </button>
            </div>
          </Animate>
        </section>

        {publicMenuVideoId && (
          <Animate type={AnimationTypes.zoomIn} duration="1.3s">
            <section className="mx-auto max-w-[1280px] px-6 pb-10">
              <LandingYoutubeFrame
                youtubeUrl={publicMenuYoutubeUrl}
                title={t("digital_menu.carousel_title")}
                aspectRatio="9 / 19.5"
                frameWrapperClassName="max-w-[320px] mx-auto"
              />
            </section>
          </Animate>
        )}

        <Animate type={AnimationTypes.slideUpIn} duration="1.3s">
          <section id="plans" className="py-4 mb-5">
            <Pricing showButton={true} />
          </section>
        </Animate>
      </main>

      <Animate type={AnimationTypes.slideUpIn} duration="1.2s" delay="0.2s">
        <div className="fixed inset-x-0 bottom-0 z-50 grid grid-cols-1 gap-2 border-t border-orange-200 bg-white/95 p-3 shadow-[0_-8px_24px_rgba(15,23,42,0.12)] backdrop-blur md:hidden">
          <button
            onClick={() => handleLeadFocus("mobile_sticky_setup")}
            className="flex min-h-12 w-full cursor-pointer items-center justify-center rounded-xl bg-gradient-to-r from-red-600 to-orange-500 px-3 py-2 text-center text-sm font-black leading-tight text-white"
            data-interaction-description-key="interactionDescriptions:pages.landPage.digitalMenuLandingPage.button.handleLeadFocusMobileStickySetup"
          >
            {t("digital_menu.mobile_sticky_done_for_you")}
          </button>

          <button
            onClick={() => handleCreateAccount("mobile_sticky_self_service")}
            className="flex min-h-12 w-full cursor-pointer items-center justify-center rounded-xl bg-gradient-to-r from-sky-600 to-indigo-600 px-3 py-2 text-center text-sm font-black leading-tight text-white shadow-md shadow-sky-500/20"
            data-interaction-description-key="interactionDescriptions:pages.landPage.digitalMenuLandingPage.button.handleCreateAccountMobileStickySelfService"
          >
            {t("digital_menu.mobile_sticky_create")}
          </button>
        </div>
      </Animate>
    </div>
  )
}
